import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

export default function CreateLeadDialog({ open, onClose, onCreated }) {
  const [masters, setMasters] = useState({});
  const [form, setForm] = useState({
    company_name: "",
    website_url: "",
    client_poc_name: "",
    client_poc_email: "",
    client_poc_number: "",
    poc_designation: "",
    category: "",
    lead_source: "",
    sales_poc: "",
    remarks: "",
    seller_tag: "",
  });
  const [saving, setSaving] = useState(false);
  const [sellerTagError, setSellerTagError] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data } = await api.get("/masters?active_only=true");
      const grouped = {};
      data.forEach((m) => {
        grouped[m.type] = grouped[m.type] || [];
        grouped[m.type].push(m);
      });
      setMasters(grouped);
    })();
  }, [open]);

  if (!open) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) {
      toast.error("Company name is required");
      return;
    }
    if (!form.seller_tag) {
      // New leads start in the "New" stage, where Seller Tag is optional.
    }
    setSellerTagError("");
    setSaving(true);
    try {
      const payload = { ...form, seller_tag: form.seller_tag ? Number(form.seller_tag) : null };
      const { data } = await api.post("/leads", payload);
      toast.success("Lead created");
      const sso = data?._sso_sync?.toast;
      if (sso) { if (sso.level === "warning") toast.warning(sso.message); else toast.error(sso.message); }
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="create-lead-dialog"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Create new lead</h3>
            <p className="text-xs text-slate-500 mt-0.5">Captures the minimum required for stage “New”.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100">
            <X size={16} />
          </button>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Company name *" testid="field-company-name">
            <input
              value={form.company_name}
              onChange={(e) => update("company_name", e.target.value)}
              required
              className="input"
            />
          </Field>
          <Field label="Website URL" testid="field-website-url">
            <input value={form.website_url} onChange={(e) => update("website_url", e.target.value)} placeholder="https://" className="input" />
          </Field>
          <Field label="Client POC name" testid="field-poc-name">
            <input value={form.client_poc_name} onChange={(e) => update("client_poc_name", e.target.value)} className="input" />
          </Field>
          <Field label="POC designation" testid="field-poc-designation">
            <input value={form.poc_designation} onChange={(e) => update("poc_designation", e.target.value)} className="input" />
          </Field>
          <Field label="POC email" testid="field-poc-email">
            <input type="email" value={form.client_poc_email} onChange={(e) => update("client_poc_email", e.target.value)} className="input" />
          </Field>
          <Field label="POC phone" testid="field-poc-phone">
            <input value={form.client_poc_number} onChange={(e) => update("client_poc_number", e.target.value)} className="input" />
          </Field>
          <Field label="Category" testid="field-category">
            <select value={form.category} onChange={(e) => update("category", e.target.value)} className="input">
              <option value="">Select…</option>
              {(masters.category || []).map((m) => <option key={m.id} value={m.value}>{m.value}</option>)}
            </select>
          </Field>
          <Field label="Seller Tag" testid="field-seller-tag">
            <select
              data-testid="seller-tag-select"
              value={form.seller_tag}
              onChange={(e) => { update("seller_tag", e.target.value); if (e.target.value) setSellerTagError(""); }}
              className="input"
            >
              <option value="">Select seller tag</option>
              <option value="1">Brand</option>
              <option value="2">Magic</option>
              <option value="3">Drop-Shipper</option>
              <option value="4">Trader</option>
              <option value="5">Aggregator</option>
            </select>
            {sellerTagError && <p data-testid="seller-tag-error" className="text-xs text-red-600 mt-1">{sellerTagError}</p>}
          </Field>
          <Field label="Lead source" testid="field-lead-source">
            <select value={form.lead_source} onChange={(e) => update("lead_source", e.target.value)} className="input">
              <option value="">Select…</option>
              {(masters.lead_source || []).map((m) => <option key={m.id} value={m.value}>{m.value}</option>)}
            </select>
          </Field>
          <Field label="Pipeline" testid="field-sales-poc">
            <select value={form.sales_poc} onChange={(e) => update("sales_poc", e.target.value)} className="input">
              <option value="">Select…</option>
              {(masters.sales_poc || []).map((m) => <option key={m.id} value={m.value}>{m.value}</option>)}
            </select>
          </Field>
          <Field label="Remarks" full testid="field-remarks">
            <textarea rows={3} value={form.remarks} onChange={(e) => update("remarks", e.target.value)} className="input" />
          </Field>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-slate-200 bg-white hover:bg-slate-50">
            Cancel
          </button>
          <button
            type="submit"
            data-testid="create-lead-submit"
            disabled={saving}
            className="px-4 py-2 text-sm rounded-md bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-medium"
          >
            {saving ? "Creating…" : "Create lead"}
          </button>
        </div>
      </form>

      <style>{`
        .input { width:100%; padding: 0.55rem 0.7rem; border: 1px solid rgb(226 232 240); border-radius: 0.375rem; font-size: 0.875rem; outline: none; background: white; }
        .input:focus { border-color: rgb(249 115 22); box-shadow: 0 0 0 2px rgba(249,115,22,0.2); }
      `}</style>
    </div>
  );
}

function Field({ label, children, full, testid }) {
  return (
    <div className={full ? "sm:col-span-2" : ""} data-testid={testid}>
      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}
